import axios from "axios";
import { baseRateCalculator } from "src/config";
import { FuelRates } from "src/types/fuel-rates";
import { msalInstance } from "src/lib/msal";
import { LoadedMiles } from "src/types/loaded-miles";

const rateCalculatorInstance = axios.create({
    baseURL: baseRateCalculator,
});

// AUTHENTICATION
rateCalculatorInstance.interceptors.request.use(
    async (config) => {
        const account = msalInstance.getAllAccounts()[0];
        if (account) {
            const { accessToken } = await msalInstance.acquireTokenSilent({
                scopes: ["User.Read"],
                account: account,
            });

            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

// SCHEMAS
type GetFuelRates = {
    status: "success" | "error" | "fail";
    data: {
        fuelRates: FuelRates[];
    };
    message: string;
};

type GetLoadedMiles = {
    status: "success" | "error" | "fail";
    data: {
        loadedMiles: LoadedMiles;
    };
    message: string;
};

// GET FUEL
export async function GetFuelRates() {
    const { data } =
        await rateCalculatorInstance.get<GetFuelRates>(`/fuel-rates`);

    if (data.status === "error") {
        throw new Error(data.message);
    }

    return data.data.fuelRates;
}

type Location = {
    city: string;
    state: string;
    zipCode: string;
};

type GetLocations = {
    status: "success" | "error" | "fail";
    data: {
        locations: Location[];
    };
    message: string;
};

//GET LOCATIONS
export async function GetLocations(search: string) {
    const { data } = await rateCalculatorInstance.get<GetLocations>(
        `/rc/locations/${search}`,
    );
    return data.data.locations;
}

//GET LOADED MILES
export async function GetLoadedMiles(origin: string, destination: string) {
    const { data } = await rateCalculatorInstance.get<GetLoadedMiles>(
        `/loaded-miles/${origin}/${destination}`,
    );
    return data.data.loadedMiles;
}

//GET BID RATES
export async function PostFormBidRates(data: {
    LoadId: string;
    PickupTime: Date | null;
    DeliveryTime: Date | null;
    EquipmentType: string;
    Miles: number;

    OriginCity: string;
    OriginState: string;
    OriginCountry: string;
    OriginZip: string;

    DestinationCity: string;
    DestinationState: string;
    DestinationCountry: string;
    DestinationZip: string;

    UPN: string;
    MilesFromAPI: boolean;
}) {
    const { data: responseData } = await rateCalculatorInstance.post(
        `/rc/avrl-rate`,
        data,
    );
    return responseData;
}

//GET RATE HISTORY
export async function PostFormRateHistory(data: {
    CurrentFuel: number;
    LoadedMiles: number;
    EmptyToOrigin: number;
    EmptyAfterDelivery: number;
    TotalEstimatedMiles: number;
    OriginZip: string;
    DestinationZip: string;
    UPN: string;
    MilesFromAPI: boolean;
}) {
    const { data: responseData } = await rateCalculatorInstance.post(
        `/rate-calculator/rate-data`,
        data,
    );
    return responseData;
}

//LOG USER FEEDBACK
export async function PostUserFeedback(data: {
    FeedbackText: string;
    UPN: string | undefined;
    HighBid: boolean;
    LowBid: boolean;
    AvgBid: boolean;
    BidRates: (number | undefined)[] | null;
    FormPayload: {};
}) {
    return rateCalculatorInstance.post(`/feedback`, data);
}
